/**
 * Referral (`ref`) and coupon query params belong to one event. Copying them
 * onto another event (related-event carousel, business landing, navigate-with-
 * query) is what sent Q' Lokura's Landing code on Fiesta checkouts.
 */

export const EVENT_SCOPED_QUERY_KEYS = ['ref', 'coupon'] as const;

export const referralStorageKey = (eventId: string) => `${eventId}_referral`;

export const readStoredReferral = (eventId: string): string | null => {
  try {
    return localStorage.getItem(referralStorageKey(eventId));
  } catch {
    return null;
  }
};

export const writeStoredReferral = (eventId: string, code: string) => {
  try {
    localStorage.setItem(referralStorageKey(eventId), code);
  } catch {
    // Private mode / quota — checkout still reads `?ref=` from the URL.
  }
};

export const clearStoredReferral = (eventId: string) => {
  try {
    localStorage.removeItem(referralStorageKey(eventId));
  } catch {
    // ignore storage errors
  }
};

export const getEventSlugFromPath = (path: string): string | null => {
  const match = path.split('?')[0].match(/^\/events\/([^/]+)/);
  return match ? match[1] : null;
};

export const shouldPreserveEventScopedQuery = (
  fromPath: string,
  toPath: string,
): boolean => {
  const fromSlug = getEventSlugFromPath(fromPath);
  const toSlug = getEventSlugFromPath(toPath);
  return Boolean(fromSlug && toSlug && fromSlug === toSlug);
};

export const stripEventScopedParams = (search: string): string => {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) {
    return '';
  }
  const params = new URLSearchParams(raw);
  EVENT_SCOPED_QUERY_KEYS.forEach((key) => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : '';
};

export const inheritedSearchForTarget = (
  currentPathname: string,
  currentSearch: string,
  targetPathname: string,
): string => (
  shouldPreserveEventScopedQuery(currentPathname, targetPathname)
    ? currentSearch
    : stripEventScopedParams(currentSearch)
);

export const resolvePathname = (currentPathname: string, targetPathname: string): string => {
  if (targetPathname.startsWith('/')) {
    return targetPathname;
  }
  const base = currentPathname.endsWith('/')
    ? currentPathname
    : `${currentPathname.split('/').slice(0, -1).join('/')}/`;
  return new URL(targetPathname, `https://placeholder.local${base}`).pathname;
};
